import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const NAV_ITEMS = [
  { id: 'quick-start', label: 'Quick Start' },
  { id: 'cli-reference', label: 'CLI Reference' },
  { id: 'rule-library', label: 'Rule Library' },
  { id: 'integrations', label: 'Integrations' },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <Box
      component="pre"
      sx={{
        bgcolor: '#1a1a1a',
        color: '#e2e8f0',
        borderRadius: 2,
        p: 2.5,
        overflowX: 'auto',
        fontFamily: '"Fira Code", "Cascadia Code", "Courier New", monospace',
        fontSize: '0.85rem',
        lineHeight: 1.7,
        border: '1px solid rgba(255,255,255,0.08)',
        my: 2,
      }}
    >
      {children}
    </Box>
  );
}

function SectionChip({ label }: { label: string }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{ bgcolor: 'rgba(255,117,24,0.1)', color: PRIMARY, fontWeight: 600, mb: 2 }}
    />
  );
}

export function Docs() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);

  const scrollTo = (id: string) => {
    navigate(`/docs#${id}`);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: DARK_BG, py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 36 }} />
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700 }}>
              BaaStream Documentation
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 600 }}>
            Everything you need to run healthcare compliance scans, understand CLI commands, and integrate BaaStream into your pipeline.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Sidebar nav */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ position: 'sticky', top: 80 }}>
              <Typography variant="overline" sx={{ color: DARK, fontWeight: 700, letterSpacing: 1 }}>
                On this page
              </Typography>
              <List dense disablePadding sx={{ mt: 1 }}>
                {NAV_ITEMS.map((item) => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    onClick={() => scrollTo(item.id)}
                    sx={{
                      py: 0.5,
                      px: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,117,24,0.08)' },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ variant: 'body2', color: DARK, fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Main content */}
          <Grid size={{ xs: 12, md: 9 }}>

            {/* ── Quick Start ── */}
            <Box id="quick-start" sx={{ mb: 8, scrollMarginTop: 80 }}>
              <SectionChip label="Quick Start" />
              <Typography variant="h4" sx={{ color: DARK, fontWeight: 700, mb: 1 }}>
                Quick Start
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Get your first compliance scan running in under 2 minutes.
              </Typography>

              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                1. Create an account & get your token
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Sign up at <Box component="span" sx={{ color: PRIMARY, fontWeight: 600 }}>baastream.com</Box>, log in to your dashboard, and navigate to <strong>CLI Token</strong> to generate your token.
              </Typography>
              <CodeBlock>{`export BAASTREAM_TOKEN=your_token_here`}</CodeBlock>

              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                2. Install the CLI
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Install the BaaStream CLI with a single command. Supports macOS, Linux, and Windows.
              </Typography>
              <CodeBlock>{`curl -sSL https://raw.githubusercontent.com/amulyakashyap09/baastream-public/master/install.sh | sh`}</CodeBlock>

              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                3. Run your first scan
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Point the scanner at a config file, FHIR resource, or directory.
              </Typography>
              <CodeBlock>{`./baastream scan fhir_server_config.json`}</CodeBlock>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Results appear instantly in your terminal and are synced to the BaaStream dashboard in real time.
              </Typography>
            </Box>

            <Divider sx={{ mb: 8 }} />

            {/* ── CLI Reference ── */}
            <Box id="cli-reference" sx={{ mb: 8, scrollMarginTop: 80 }}>
              <SectionChip label="CLI Reference" />
              <Typography variant="h4" sx={{ color: DARK, fontWeight: 700, mb: 1 }}>
                CLI Reference
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Full reference for all BaaStream CLI commands and flags.
              </Typography>

              {/* scan */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                baastream scan
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Run a compliance scan against a file, directory, or glob pattern.
              </Typography>
              <CodeBlock>{`baastream scan <target> [flags]

Examples:
  baastream scan ./config.json
  baastream scan ./fhir-resources/ --framework hipaa
  baastream scan ./logs/ --framework soc2 --output json`}</CodeBlock>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f8f8' }}>
                      <TableCell sx={{ fontWeight: 700, color: DARK }}>Flag</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: DARK }}>Default</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: DARK }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['--framework', 'all', 'Compliance framework to run: hipaa, fhir, hitrust, soc2, gdpr, iso27001'],
                      ['--output', 'table', 'Output format: table, json, pdf, summary'],
                      ['--token', '$BAASTREAM_TOKEN', 'Override the CLI token for this run'],
                      ['--fail-on', 'critical', 'Exit code 1 if any finding matches severity: critical, high, medium'],
                      ['--ignore', '—', 'Comma-separated rule IDs to skip (e.g. HIPAA-001,FHIR-012)'],
                      ['--recursive', 'false', 'Recursively scan all files in a directory'],
                      ['--verbose', 'false', 'Show detailed rule-by-rule output'],
                    ].map(([flag, def, desc]) => (
                      <TableRow key={flag}>
                        <TableCell>
                          <Box component="code" sx={{ color: PRIMARY, fontWeight: 600, fontSize: '0.82rem' }}>{flag}</Box>
                        </TableCell>
                        <TableCell>
                          <Box component="code" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{def}</Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{desc}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* auth */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                baastream auth
              </Typography>
              <CodeBlock>{`baastream auth --token <your_token>   # Set token for the current machine
baastream auth --status               # Show current auth status
baastream auth --revoke               # Revoke the stored token`}</CodeBlock>

              {/* version */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                baastream version
              </Typography>
              <CodeBlock>{`baastream version    # Print CLI version and build info`}</CodeBlock>

              {/* Environment variables */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 3, mb: 1 }}>
                Environment Variables
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f8f8' }}>
                      <TableCell sx={{ fontWeight: 700, color: DARK }}>Variable</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: DARK }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['BAASTREAM_TOKEN', 'CLI authentication token (required)'],
                      ['BAASTREAM_OUTPUT', 'Default output format — overridden by --output flag'],
                      ['BAASTREAM_FRAMEWORK', 'Default framework — overridden by --framework flag'],
                      ['BAASTREAM_FAIL_ON', 'Default fail-on severity — overridden by --fail-on flag'],
                    ].map(([v, d]) => (
                      <TableRow key={v}>
                        <TableCell>
                          <Box component="code" sx={{ color: PRIMARY, fontWeight: 600, fontSize: '0.82rem' }}>{v}</Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{d}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ mb: 8 }} />

            {/* ── Rule Library ── */}
            <Box id="rule-library" sx={{ mb: 8, scrollMarginTop: 80 }}>
              <SectionChip label="Rule Library" />
              <Typography variant="h4" sx={{ color: DARK, fontWeight: 700, mb: 1 }}>
                Rule Library
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                BaaStream ships with 245+ built-in rules across 6 compliance frameworks. Each scan runs the rules relevant to the selected framework and target file type.
              </Typography>

              {[
                {
                  name: 'HIPAA',
                  color: '#2563eb',
                  rules: '85+',
                  desc: 'Health Insurance Portability and Accountability Act — covers Privacy Rule (164.502), Security Rule (164.308, 164.312), and Breach Notification Rule.',
                  examples: ['Access control enforcement (164.312(a))', 'Audit log completeness (164.308(a)(1))', 'Encryption at rest and in transit (164.312(e))', 'Minimum necessary data access', 'PHI de-identification validation'],
                },
                {
                  name: 'FHIR',
                  color: '#16a34a',
                  rules: '40+',
                  desc: 'Fast Healthcare Interoperability Resources R4/R5 — validates resource conformance, HL7 standards, and secure data exchange.',
                  examples: ['Resource profile conformance', 'Required field presence', 'Terminology binding validation', 'Reference integrity checks', 'Capability statement coverage'],
                },
                {
                  name: 'HITRUST',
                  color: '#9333ea',
                  rules: '35+',
                  desc: 'Health Information Trust Alliance CSF — maps to control categories 01–19 covering information protection program, endpoint protection, and network protection.',
                  examples: ['Information protection policy', 'Endpoint protection controls', 'Network access control', 'Third-party assurance', 'Audit logging and monitoring'],
                },
                {
                  name: 'SOC 2',
                  color: '#dc2626',
                  rules: '30+',
                  desc: 'Service Organization Control 2 — Trust Services Criteria covering Security, Availability, Processing Integrity, Confidentiality, and Privacy.',
                  examples: ['Logical access controls', 'Change management processes', 'Incident response procedures', 'System monitoring', 'Vendor risk management'],
                },
                {
                  name: 'GDPR',
                  color: '#ea580c',
                  rules: '25+',
                  desc: 'General Data Protection Regulation — covers lawful basis, data subject rights, data minimisation, and cross-border transfer rules.',
                  examples: ['Consent management', 'Data subject access rights', 'Data retention limits', 'Breach notification timelines', 'Cross-border transfer safeguards'],
                },
                {
                  name: 'ISO 27001',
                  color: '#0891b2',
                  rules: '20+',
                  desc: 'Information Security Management — Annex A controls across 14 domains covering access control, cryptography, operations security, and supplier relationships.',
                  examples: ['Asset management controls', 'Cryptography policy', 'Operations security', 'Communications security', 'Supplier relationship controls'],
                },
              ].map((fw) => (
                <Box key={fw.name} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Chip label={fw.name} size="small" sx={{ bgcolor: fw.color, color: '#fff', fontWeight: 700 }} />
                    <Chip label={`${fw.rules} rules`} size="small" variant="outlined" sx={{ color: fw.color, borderColor: fw.color, fontWeight: 600 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {fw.desc}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, mb: 0.5 }}>
                    Example checks:
                  </Typography>
                  <List dense disablePadding>
                    {fw.examples.map((ex) => (
                      <ListItem key={ex} disablePadding sx={{ pl: 1 }}>
                        <ListItemText
                          primary={`• ${ex}`}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <CodeBlock>{`baastream scan ./config/ --framework ${fw.name.toLowerCase().replace(' ', '')}`}</CodeBlock>
                </Box>
              ))}
            </Box>

            <Divider sx={{ mb: 8 }} />

            {/* ── Integrations ── */}
            <Box id="integrations" sx={{ mb: 4, scrollMarginTop: 80 }}>
              <SectionChip label="Integrations" />
              <Typography variant="h4" sx={{ color: DARK, fontWeight: 700, mb: 1 }}>
                Integrations
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Embed BaaStream compliance checks directly into your CI/CD pipeline. Every scan result is synced to your dashboard automatically.
              </Typography>

              {/* GitHub Actions */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mb: 1 }}>
                GitHub Actions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add the following step to your workflow. Store your token as a repository secret named <Box component="code" sx={{ color: PRIMARY }}>BAASTREAM_TOKEN</Box>.
              </Typography>
              <CodeBlock>{`# .github/workflows/compliance.yml
name: Compliance Scan

on: [push, pull_request]

jobs:
  baastream:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Install BaaStream CLI
        run: curl -sSL https://raw.githubusercontent.com/amulyakashyap09/baastream-public/master/install.sh | sh

      - name: Run compliance scan
        env:
          BAASTREAM_TOKEN: \${{ secrets.BAASTREAM_TOKEN }}
        run: ./baastream scan ./config/ --framework hipaa --fail-on critical`}</CodeBlock>

              {/* Jenkins */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 4, mb: 1 }}>
                Jenkins
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add a compliance stage to your <Box component="code" sx={{ color: PRIMARY }}>Jenkinsfile</Box>. Store the token as a Jenkins credential.
              </Typography>
              <CodeBlock>{`// Jenkinsfile
pipeline {
  agent any

  environment {
    BAASTREAM_TOKEN = credentials('baastream-token')
  }

  stages {
    stage('Compliance Scan') {
      steps {
        sh 'curl -sSL https://raw.githubusercontent.com/amulyakashyap09/baastream-public/master/install.sh | sh'
        sh './baastream scan ./config/ --framework hipaa --output json'
      }
    }
  }
}`}</CodeBlock>

              {/* Terraform */}
              <Typography variant="h6" sx={{ color: DARK, fontWeight: 600, mt: 4, mb: 1 }}>
                Terraform (pre-plan hook)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Scan Terraform configs before <Box component="code" sx={{ color: PRIMARY }}>plan</Box> or <Box component="code" sx={{ color: PRIMARY }}>apply</Box> using a local-exec provisioner or a Makefile hook.
              </Typography>
              <CodeBlock>{`# Makefile
pre-plan:
	curl -sSL https://raw.githubusercontent.com/amulyakashyap09/baastream-public/master/install.sh | sh
	./baastream scan ./infra/ --framework hipaa --fail-on high

plan: pre-plan
	terraform plan`}</CodeBlock>

              {/* General tip */}
              <Paper
                variant="outlined"
                sx={{ p: 3, mt: 4, borderColor: 'rgba(255,117,24,0.3)', bgcolor: 'rgba(255,117,24,0.04)' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: DARK, mb: 0.5 }}>
                  Tip: fail fast on critical findings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use <Box component="code" sx={{ color: PRIMARY }}>--fail-on critical</Box> so your pipeline exits with a non-zero code only on critical violations. Use <Box component="code" sx={{ color: PRIMARY }}>--fail-on high</Box> for stricter enforcement. Results are always synced to the dashboard regardless of exit code.
                </Typography>
              </Paper>
            </Box>

          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
            © {new Date().getFullYear()} Monks Do Work. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
